import React from 'react';

import { useDevConnectionState } from './../../ricos-context';
import type { GetWixClientPromise } from './../request-service-provider/use-get-wix-client-promise';
import { useGetWixClientPromise } from './../request-service-provider/use-get-wix-client-promise';
import { wrapWithNetworkConditions } from './../../wix-rich-content-common/devTools/wrap-with-network-conditions';

const RequestServiceContext = React.createContext<RequestService>(
  null as unknown as RequestService
);

export type RequestService = {
  makeRequest(input: string, init?: RequestInit): Promise<Response>;
};

export const RequestServiceProvider: React.FC<
  React.PropsWithChildren<{
    getWixClientPromise?: GetWixClientPromise;
  }>
> = ({ children, getWixClientPromise: existingGetWixClientPromise }) => {
  const connectionState = useDevConnectionState();
  const { getWixClientPromise } = useGetWixClientPromise('', existingGetWixClientPromise);

  const service = React.useMemo<RequestService>(() => {
    return {
      makeRequest: wrapWithNetworkConditions(
        async (relativeUrl: string, options: RequestInit) => {
          const wixClient = await getWixClientPromise();
          return wixClient.fetchWithAuth(relativeUrl, options);
        },
        () => connectionState
      ),
    };
  }, [getWixClientPromise, connectionState]);

  return (
    <RequestServiceContext.Provider value={service}>{children}</RequestServiceContext.Provider>
  );
};

export const useRequestService = () => {
  const context = React.useContext(RequestServiceContext);

  if (!context) {
    throw new Error('useRequestService must be used within a RequestServiceProvider');
  }

  return context;
};
